import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { FileText, Upload, Filter, Trash2, AlertCircle, Lock, Unlock, LogIn, Sparkles, Check, X, Edit2, FolderInput, Folder, Download, FileStack, Camera } from 'lucide-react';
import CameraCapture from '../components/CameraCapture';
import { brandColors } from '../components/Brand';
import api from '../api/api';
import { toast } from 'sonner';
import { renderSafe, normalizeSuggestions, normalizeTags } from '../utils/safeRender';
import FolderTree from '../components/FolderTree';

const Documents = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [virtualFolders, setVirtualFolders] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  
  // AI Metadata Autopilot state
  const [aiAutopilotOpen, setAiAutopilotOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('file');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadDocType, setUploadDocType] = useState('Other');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadVisibility, setUploadVisibility] = useState('GLOBAL');
  const [uploadWorkflowId, setUploadWorkflowId] = useState('none');
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Check-in form state
  const [checkinFile, setCheckinFile] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  
  // Document AI state
  const [processingDocAI, setProcessingDocAI] = useState(false);

  // Bulk upload + selection state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkResults, setBulkResults] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Physical folders state
  const [physicalFolderId, setPhysicalFolderId] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetDoc, setMoveTargetDoc] = useState(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState(null);
  const [allFoldersFlat, setAllFoldersFlat] = useState([]);

  useEffect(() => {
    document.title = 'ReasonDMS | Documents';
    loadDocuments();
    loadVirtualFolders();
  }, [filterType, physicalFolderId]);

  useEffect(() => {
    api.listWorkflows(getAuthHeaders())
      .then((r) => setActiveWorkflows((r.data || []).filter((w) => w.is_active)))
      .catch(() => setActiveWorkflows([]));
    // eslint-disable-next-line
  }, []);

  const loadVirtualFolders = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await api.getVirtualFolderSuggestions(headers);
      setVirtualFolders(response.data);
    } catch (error) {
      console.log('Virtual folders not available');
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = {};
      if (filterType !== 'all') params.doc_type = filterType;
      if (physicalFolderId) params.folder_id = physicalFolderId;
      const response = await api.listDocuments(params, headers);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  // Flatten folder tree for the "Move to folder" select
  const flattenTree = (nodes, depth = 0, acc = []) => {
    for (const n of nodes || []) {
      acc.push({ id: n.id, label: `${'— '.repeat(depth)}${n.name}`, path: n.path });
      if (n.children?.length) flattenTree(n.children, depth + 1, acc);
    }
    return acc;
  };

  const loadAllFoldersForMove = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await api.listFolders(headers);
      setAllFoldersFlat(flattenTree(res.data?.folders || []));
    } catch (e) {
      toast.error('Failed to load folders');
    }
  };

  const openMoveDialog = (doc) => {
    setMoveTargetDoc(doc);
    setMoveTargetFolderId(doc.folder_id || null);
    setMoveDialogOpen(true);
    loadAllFoldersForMove();
  };

  const submitMove = async () => {
    if (!moveTargetDoc) return;
    try {
      const headers = getAuthHeaders();
      await api.moveDocument(moveTargetDoc.id, moveTargetFolderId || null, headers);
      toast.success(moveTargetFolderId ? 'Document moved' : 'Document moved to root');
      setMoveDialogOpen(false);
      setMoveTargetDoc(null);
      loadDocuments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    }
  };

  const handleFileSelect = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;

    if (list.length === 1) {
      // Preserve existing single-file flow
      const file = list[0];
      setUploadFile(file);
      setUploadTitle(file.name.replace(/\.[^/.]+$/, ""));
      setBulkFiles([]);
      toast.info('Analyzing document...');
    } else {
      // Bulk mode
      setBulkFiles(list);
      setUploadFile(null);
      setUploadTitle('');
      toast.info(`${list.length} files queued for bulk upload`);
    }
  };

  const inferTypeFromName = (name = '') => {
    const ext = name.toLowerCase().split('.').pop();
    return ({ pdf: 'PDF', docx: 'Word', doc: 'Word', xlsx: 'Excel', xls: 'Excel',
      png: 'Image', jpg: 'Image', jpeg: 'Image', tif: 'Image', tiff: 'Image' })[ext] || 'Other';
  };

  const removeBulkFile = (idx) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Optionally start the chosen approval workflow on a freshly uploaded document.
  // No-op unless a workflow was selected AND the document is GLOBAL.
  const maybeStartWorkflowOnUpload = async (docId, headers) => {
    if (!docId || !uploadWorkflowId || uploadWorkflowId === 'none' || uploadVisibility !== 'GLOBAL') return false;
    try {
      await api.startWorkflow(docId, uploadWorkflowId, headers);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Uploaded, but the workflow could not be started');
      return false;
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBulkProgress({ current: 0, total: bulkFiles.length });
    setBulkResults(null);

    try {
      const formData = new FormData();
      bulkFiles.forEach((f) => formData.append('files', f));
      formData.append('doc_type', uploadDocType || 'Other');
      formData.append('tags', JSON.stringify(uploadTags.split(',').map(t => t.trim()).filter(Boolean)));
      formData.append('visibility', uploadVisibility);
      if (physicalFolderId) formData.append('folder_id', physicalFolderId);

      const headers = getAuthHeaders();
      const response = await api.bulkUploadDocuments(formData, headers, (p) => {
        if (p.total) {
          const pct = p.loaded / p.total;
          setBulkProgress({ current: Math.floor(pct * bulkFiles.length), total: bulkFiles.length });
        }
      });

      setBulkProgress({ current: bulkFiles.length, total: bulkFiles.length });
      setBulkResults(response.data);
      const { queued, errors, results } = response.data;
      toast.success(`Uploaded ${queued} file(s)${errors ? `, ${errors} error(s)` : ''}`);

      // Optionally start the selected approval workflow on each uploaded doc (GLOBAL only).
      if (uploadWorkflowId && uploadWorkflowId !== 'none' && uploadVisibility === 'GLOBAL') {
        const headersWf = getAuthHeaders();
        const ids = (results || []).filter((r) => r.doc_id).map((r) => r.doc_id);
        const started = await Promise.allSettled(
          ids.map((docId) => api.startWorkflow(docId, uploadWorkflowId, headersWf))
        );
        const ok = started.filter((s) => s.status === 'fulfilled').length;
        if (ok > 0) toast.success(`Approval workflow started on ${ok} document(s)`);
      }
      setBulkFiles([]);
      loadDocuments();

      // Auto-close after a short delay if no errors. Slightly longer so users
      // see "Completed · N of N files" before the dialog closes.
      if (!errors) {
        setTimeout(() => {
          setUploadDialogOpen(false);
          resetUploadForm();
        }, 1500);
      }
    } catch (err) {
      console.error('Bulk upload failed:', err);
      toast.error(err?.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    // Bulk path
    if (bulkFiles.length > 0) {
      return handleBulkUpload();
    }

    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);
      formData.append('description', uploadDescription);
      formData.append('doc_type', uploadDocType);
      formData.append('tags', JSON.stringify(uploadTags.split(',').map(t => t.trim()).filter(Boolean)));
      formData.append('visibility', uploadVisibility);
      if (physicalFolderId) formData.append('folder_id', physicalFolderId);

      const headers = getAuthHeaders();
      const response = await api.uploadDocument(formData, headers);
      
      toast.success('Document uploaded! AI is analyzing for metadata suggestions...');
      const docId = response.data.id;

      // Optionally kick off an approval workflow right away (GLOBAL docs only).
      const wfStarted = await maybeStartWorkflowOnUpload(docId, headers);
      if (wfStarted) toast.success('Approval workflow started');

      setUploadDialogOpen(false);
      resetUploadForm();
      loadDocuments();
      
      // Open AI Autopilot dialog and poll for suggestions
      setUploadedDocId(docId);
      setAiAutopilotOpen(true);
      pollForSuggestions(docId);
      
      // Trigger Document AI processing in background (if image/PDF)
      const fileType = uploadFile.type;
      if (fileType.startsWith('image/') || fileType === 'application/pdf') {
        triggerDocumentAIProcessing(docId);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const pollForSuggestions = async (docId) => {
    setLoadingSuggestions(true);
    const headers = getAuthHeaders();
    let attempts = 0;
    const maxAttempts = 15; // Increased attempts
    
    const checkSuggestions = async () => {
      try {
        const response = await api.getMetadataSuggestions(docId, headers);
        console.log('AI Suggestions response:', response.data); // Debug log
        
        if (response.data && response.data.has_suggestions) {
          // Use normalizeSuggestions to ensure safe rendering
          const safeSuggestions = normalizeSuggestions(response.data.suggestions);
          setAiSuggestions(safeSuggestions);
          setEditedValues(safeSuggestions);
          setLoadingSuggestions(false);
          return true;
        }
        
        // Also check if suggestions exist directly in response
        if (response.data && (response.data.title || response.data.doc_type || response.data.tags)) {
          // Use normalizeSuggestions for direct response format too
          const safeSuggestions = normalizeSuggestions(response.data);
          setAiSuggestions(safeSuggestions);
          setEditedValues(safeSuggestions);
          setLoadingSuggestions(false);
          return true;
        }
      } catch (error) {
        console.log('Suggestions check attempt:', attempts, error.message);
      }
      return false;
    };
    
    // Check immediately first
    const foundImmediately = await checkSuggestions();
    if (foundImmediately) return;
    
    // Poll every 2 seconds for up to 30 seconds
    const poll = setInterval(async () => {
      attempts++;
      const found = await checkSuggestions();
      if (found || attempts >= maxAttempts) {
        clearInterval(poll);
        setLoadingSuggestions(false);
        if (!found && attempts >= maxAttempts) {
          // Show explicit message instead of silent failure
          toast.info('AI analysis complete. Suggestions may be available in document details.');
        }
      }
    }, 2000);
  };

  const triggerDocumentAIProcessing = async (docId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/document-ai/process/${docId}`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(`Document AI processed: ${result.stages_completed?.length || 0} stages completed`);
        }
      }
    } catch (error) {
      // Silent fail - Document AI is optional enhancement
      console.log('Document AI processing skipped:', error.message);
    }
  };

  const handleAcceptSuggestions = async () => {
    if (!uploadedDocId) return;
    
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      
      if (editedValues.title) formData.append('title', editedValues.title);
      if (editedValues.doc_type) formData.append('doc_type', editedValues.doc_type);
      if (editedValues.tags) {
        const tags = Array.isArray(editedValues.tags) ? editedValues.tags : [editedValues.tags];
        formData.append('tags', JSON.stringify(tags));
      }
      
      await api.applyMetadata(uploadedDocId, formData, headers);
      toast.success('AI suggestions applied successfully!');
      setAiAutopilotOpen(false);
      setAiSuggestions(null);
      setUploadedDocId(null);
      setEditedValues({});
      loadDocuments();
    } catch (error) {
      console.error('Error applying suggestions:', error);
      toast.error('Failed to apply suggestions');
    }
  };

  const handleRejectSuggestions = () => {
    toast.info('AI suggestions dismissed. Document kept with original metadata.');
    setAiAutopilotOpen(false);
    setAiSuggestions(null);
    setUploadedDocId(null);
    setEditedValues({});
  };

  const handleEditField = (field, value) => {
    setEditedValues(prev => ({ ...prev, [field]: value }));
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadDocType('Other');
    setUploadTags('');
    setUploadVisibility('GLOBAL');
    setUploadWorkflowId('none');
    setBulkFiles([]);
    setBulkProgress({ current: 0, total: 0 });
    setBulkResults(null);
    setUploadMode('file');
  };

  // --- Selection + bulk-download helpers ---
  const toggleDocSelected = (docId) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };
  const clearSelection = () => setSelectedDocIds(new Set());

  const handleBulkDownload = async () => {
    if (selectedDocIds.size === 0) return;
    setBulkDownloading(true);
    try {
      const headers = getAuthHeaders();
      const ids = Array.from(selectedDocIds);
      const res = await api.bulkDownloadDocuments(ids, headers);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `documents_${ts}.zip`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ZIP of ${ids.length} document(s)`);
      clearSelection();
    } catch (err) {
      console.error('Bulk download failed:', err);
      // Blob errors hide the JSON detail; try to parse
      let msg = 'Bulk download failed';
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          msg = JSON.parse(text).detail || msg;
        } catch (e) { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleDelete = (docId) => {
    setDeleteTarget(docId);
  };

  const confirmDelete = async () => {
    const docId = deleteTarget;
    if (!docId) return;
    try {
      const headers = getAuthHeaders();
      await api.deleteDocument(docId, headers);
      api.invalidatePresignedCache(docId);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleCheckout = async (doc, e) => {
    e.stopPropagation();
    
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      await api.checkoutDocument(doc.id, headers);
      toast.success('Document checked out successfully');
      await loadDocuments();
    } catch (error) {
      console.error('Error checking out document:', error);
      toast.error(error.response?.data?.detail || 'Failed to check out document');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (e) => {
    e.preventDefault();
    
    if (!checkinFile) {
      toast.error('Please select a file to check in');
      return;
    }

    setCheckingIn(true);

    try {
      const formData = new FormData();
      formData.append('file', checkinFile);

      const headers = getAuthHeaders();
      await api.checkinDocument(selectedDoc.id, formData, headers);
      
      toast.success('Document checked in successfully');
      setCheckinDialogOpen(false);
      setCheckinFile(null);
      setSelectedDoc(null);
      loadDocuments();
    } catch (error) {
      console.error('Error checking in document:', error);
      toast.error(error.response?.data?.detail || 'Failed to check in document');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCancelCheckout = async (doc, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to cancel the checkout?')) {
      return;
    }

    try {
      setLoading(true);
      const headers = getAuthHeaders();
      await api.cancelCheckout(doc.id, headers);
      toast.success('Checkout cancelled');
      await loadDocuments();
    } catch (error) {
      console.error('Error cancelling checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel checkout');
    } finally {
      setLoading(false);
    }
  };

  const openCheckinDialog = (doc, e) => {
    e.stopPropagation();
    setSelectedDoc(doc);
    setCheckinDialogOpen(true);
  };

  const getRiskBadgeColor = (riskLevel) => {
    switch (riskLevel) {
      case 'High':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Medium':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="documents-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Documents</h1>
          <p className="text-slate-600 mt-1">Manage your document repository</p>
        </div>
        <Button
          onClick={() => setUploadDialogOpen(true)}
          data-testid="upload-document-button"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Split layout: folder tree + document list */}
      <div className="flex gap-4 items-start">
        <aside
          className="w-[220px] shrink-0 bg-white border border-slate-200 rounded-lg p-3 sticky top-4 max-h-[calc(100vh-7rem)] overflow-y-auto"
          data-testid="folder-sidebar"
        >
          <FolderTree
            selectedFolderId={physicalFolderId}
            onSelectFolder={setPhysicalFolderId}
          />
        </aside>

        <div className="flex-1 min-w-0 space-y-6">

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-slate-600" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-64" data-testid="doc-type-filter">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                <SelectItem value="Invoice">Invoices</SelectItem>
                <SelectItem value="Contract">Contracts</SelectItem>
                <SelectItem value="Policy">Policies</SelectItem>
                <SelectItem value="Technical Manual">Technical Manuals</SelectItem>
                <SelectItem value="Report">Reports</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mobile/tablet camera hint (P3-H) */}
      {(typeof window !== 'undefined' && (window.innerWidth < 1024 || 'ontouchstart' in window) && navigator.mediaDevices) && (
        <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-100 text-blue-700 rounded-lg px-3 py-2 mb-4" data-testid="camera-mobile-banner">
          <Camera className="w-4 h-4 shrink-0" />
          Tap &lsquo;Camera&rsquo; in the upload dialog to photograph and upload documents directly.
        </div>
      )}

      {/* Documents List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {documents.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No documents found</h3>
                <p className="text-slate-500 mb-4">Upload your first document to get started</p>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          documents.map((doc) => (
            <Card
              key={doc.id}
              data-testid={`document-card-${doc.id}`}
              className={`group relative hover:shadow-lg transition-shadow cursor-pointer ${selectedDocIds.has(doc.id) ? 'ring-2 ring-blue-400' : ''}`}
              onClick={() => navigate(`/documents/${doc.id}`)}
            >
              {/* Selection checkbox — visible on hover or when selected */}
              <div
                className={`absolute top-2 left-2 z-10 transition-opacity ${selectedDocIds.has(doc.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selectedDocIds.has(doc.id)}
                  onCheckedChange={() => toggleDocSelected(doc.id)}
                  data-testid={`doc-checkbox-${doc.id}`}
                  className="bg-white shadow"
                />
              </div>

              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-8 h-8 text-blue-600" />
                    {doc.is_checked_out && (
                      <Lock className="w-4 h-4 text-orange-600" title="Document is checked out" />
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full border ${getRiskBadgeColor(doc.risk_level)}`}>
                    {doc.risk_level} Risk
                  </span>
                </div>
                <CardTitle className="mt-4 line-clamp-2">{doc.title}</CardTitle>
                <p className="text-sm text-slate-500">{doc.doc_type}</p>
                {doc.is_checked_out && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    <Lock className="w-3 h-3" />
                    <span>Checked out by {doc.checked_out_by === user?.id ? 'you' : 'another user'}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {doc.description && (
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{doc.description}</p>
                )}
                
                {doc.tags && doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {doc.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {doc.tags.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                        +{doc.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-xs text-slate-500">
                    Version {doc.current_version}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/documents/${doc.id}`);
                      }}
                      data-testid={`view-doc-${doc.id}`}
                    >
                      View
                    </Button>
                    
                    {/* Check-out button - only if not checked out */}
                    {!doc.is_checked_out && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleCheckout(doc, e)}
                        data-testid={`checkout-doc-${doc.id}`}
                        className="text-blue-600 hover:text-blue-700"
                        title="Check out for editing"
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Check-in button - only if checked out by current user */}
                    {doc.is_checked_out && doc.checked_out_by === user?.id && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => openCheckinDialog(doc, e)}
                          data-testid={`checkin-doc-${doc.id}`}
                          className="text-green-600 hover:text-green-700"
                          title="Check in new version"
                        >
                          <LogIn className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => handleCancelCheckout(doc, e)}
                          data-testid={`cancel-checkout-doc-${doc.id}`}
                          className="text-orange-600 hover:text-orange-700"
                          title="Cancel checkout"
                        >
                          <AlertCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    
                    {!doc.legal_hold && !doc.is_checked_out && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMoveDialog(doc);
                        }}
                        data-testid={`move-doc-${doc.id}`}
                        className="text-amber-600 hover:text-amber-700"
                        title="Move to folder"
                      >
                        <FolderInput className="w-4 h-4" />
                      </Button>
                    )}

                    {!doc.legal_hold && !doc.is_checked_out && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc.id);
                        }}
                        data-testid={`delete-doc-${doc.id}`}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

        </div>{/* /flex-1 */}
      </div>{/* /split layout */}

      {/* Sticky bulk-action bar */}
      {selectedDocIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white shadow-2xl rounded-full px-5 py-2.5 flex items-center gap-4 z-50 border border-slate-700"
        >
          <span className="text-sm font-medium" data-testid="bulk-selected-count">
            {selectedDocIds.size} selected
          </span>
          <button
            onClick={clearSelection}
            className="text-xs text-slate-300 hover:text-white"
            data-testid="bulk-clear"
          >
            Clear
          </button>
          <div className="w-px h-5 bg-slate-700" />
          <Button
            size="sm"
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
            data-testid="bulk-download-button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {bulkDownloading ? 'Preparing ZIP…' : `Download selected (${selectedDocIds.size})`}
          </Button>
        </div>
      )}

      {/* Move-to-folder dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent data-testid="move-folder-dialog">
          <DialogHeader>
            <DialogTitle>Move "{moveTargetDoc?.title}"</DialogTitle>
            <DialogDescription>Pick a destination folder, or move to root.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-64 overflow-y-auto">
            <button
              onClick={() => setMoveTargetFolderId(null)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded text-sm border ${moveTargetFolderId === null ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 hover:bg-slate-50'}`}
              data-testid="move-target-root"
            >
              <Folder className="w-4 h-4 text-slate-500" /> All Documents (root)
            </button>
            {allFoldersFlat.map((f) => (
              <button
                key={f.id}
                onClick={() => setMoveTargetFolderId(f.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded text-sm border ${moveTargetFolderId === f.id ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid={`move-target-${f.id}`}
                title={f.path}
              >
                <Folder className="w-4 h-4 text-amber-500" /> {f.label}
              </button>
            ))}
            {allFoldersFlat.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-3">No folders yet. Create one in the left sidebar first.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)} data-testid="move-cancel">Cancel</Button>
            <Button onClick={submitMove} data-testid="move-submit">
              <FolderInput className="w-4 h-4 mr-1.5" /> Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            {/* Upload mode tabs (P3-H) */}
            <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-lg" data-testid="upload-mode-tabs">
              <button
                type="button"
                data-testid="upload-mode-file"
                onClick={() => setUploadMode('file')}
                className={`flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-md transition-colors ${uploadMode === 'file' ? 'bg-white shadow-sm font-medium' : 'text-slate-500'}`}
              >
                <Upload className="w-4 h-4" /> File
              </button>
              <button
                type="button"
                data-testid="upload-mode-camera"
                onClick={() => setUploadMode('camera')}
                className={`flex items-center justify-center gap-1.5 text-sm py-1.5 rounded-md transition-colors ${uploadMode === 'camera' ? 'bg-white shadow-sm font-medium' : 'text-slate-500'}`}
              >
                <Camera className="w-4 h-4" /> Camera
              </button>
            </div>

            {uploadMode === 'camera' && !uploadFile && (
              <CameraCapture
                onCapture={(file) => {
                  setUploadFile(file);
                  setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
                  setBulkFiles([]);
                  setUploadMode('file');
                  toast.success('Photo captured — add details and upload');
                }}
                onClose={() => setUploadMode('file')}
              />
            )}

            {uploadMode === 'file' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                File{bulkFiles.length > 1 ? `s — ${bulkFiles.length} selected (bulk mode)` : ''}
              </label>
              <Input
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.tiff,.tif,.txt"
                data-testid="upload-file-input"
                onChange={handleFileSelect}
              />
              <p className="text-xs text-slate-500 mt-1">
                Pick a single file for guided upload (with AI suggestions) or multiple files for bulk upload (max 50).
              </p>
              {uploadFile && (
                <p className="text-xs text-emerald-600 mt-1" data-testid="captured-file-name">Selected: {uploadFile.name}</p>
              )}
            </div>
            )}

            {bulkFiles.length > 1 && (
              <div className="border border-slate-200 rounded-md p-2 max-h-48 overflow-y-auto bg-slate-50" data-testid="bulk-file-list">
                <p className="text-xs font-semibold text-slate-600 mb-2">{bulkFiles.length} files queued</p>
                <ul className="space-y-1">
                  {bulkFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1" data-testid={`bulk-file-${i}`}>
                      <div className="flex items-center gap-2 truncate">
                        <FileStack className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate" title={f.name}>{f.name}</span>
                        <span className="text-slate-400 shrink-0">· {inferTypeFromName(f.name)} · {(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                      {!bulkUploading && (
                        <button
                          type="button"
                          onClick={() => removeBulkFile(i)}
                          className="text-slate-400 hover:text-red-600 shrink-0"
                          data-testid={`bulk-file-remove-${i}`}
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(bulkUploading || bulkResults) && (
              <div data-testid="bulk-progress">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                  <span>{bulkUploading ? 'Uploading…' : 'Completed'}</span>
                  <span>{bulkProgress.current} of {bulkProgress.total} files</span>
                </div>
                <Progress value={bulkProgress.total ? (bulkProgress.current / bulkProgress.total) * 100 : 0} />
                {bulkResults && bulkResults.errors > 0 && (
                  <p className="text-xs text-red-600 mt-2">{bulkResults.errors} file(s) failed. Check console for details.</p>
                )}
              </div>
            )}

            {bulkFiles.length <= 1 && (
            <>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <Input
                data-testid="upload-title-input"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Document title"
                required={bulkFiles.length === 0}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <Input
                data-testid="upload-description-input"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description (optional)"
              />
            </div>
            </>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Document Type{bulkFiles.length > 1 ? ' (applies to all)' : ''}</label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger data-testid="upload-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Invoice">Invoice</SelectItem>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Policy">Policy</SelectItem>
                  <SelectItem value="Technical Manual">Technical Manual</SelectItem>
                  <SelectItem value="Report">Report</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <Input
                data-testid="upload-tags-input"
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple tags with commas</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Visibility</label>
              <Select value={uploadVisibility} onValueChange={setUploadVisibility}>
                <SelectTrigger data-testid="upload-visibility-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GLOBAL">
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Global - Visible to all users in company
                    </div>
                  </SelectItem>
                  <SelectItem value="RESTRICTED">
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      Restricted - Only visible to you
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {uploadVisibility === 'GLOBAL' 
                  ? 'All users in your company can see this document' 
                  : 'Only you can see this document (admins can still access)'}
              </p>
            </div>

            {/* Optional: start an approval workflow immediately on upload (GLOBAL only) */}
            <div>
              <label className="block text-sm font-medium mb-2">Start approval workflow (optional)</label>
              <Select
                value={uploadWorkflowId}
                onValueChange={setUploadWorkflowId}
                disabled={uploadVisibility !== 'GLOBAL' || activeWorkflows.length === 0}
              >
                <SelectTrigger data-testid="upload-workflow-select">
                  <SelectValue placeholder={activeWorkflows.length === 0 ? 'No active workflows' : 'No workflow'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No workflow</SelectItem>
                  {activeWorkflows.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`upload-workflow-option-${w.id}`}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {uploadVisibility !== 'GLOBAL'
                  ? 'Switch visibility to Global to route this document through an approval workflow.'
                  : 'The first approval step will be assigned automatically after upload.'}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="upload-submit-button"
                disabled={uploading || bulkUploading || (bulkFiles.length === 0 && !uploadFile)}
                className="flex-1"
              >
                {bulkUploading
                  ? `Uploading ${bulkProgress.current}/${bulkProgress.total}…`
                  : uploading
                  ? 'Uploading...'
                  : bulkFiles.length > 1
                  ? `Upload ${bulkFiles.length} files`
                  : 'Upload'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading || bulkUploading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check In Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckin} className="space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-3">
                Checking in: <span className="font-semibold">{selectedDoc?.title}</span>
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Current version: {selectedDoc?.current_version}
              </p>
              <label className="block text-sm font-medium mb-2">New Version File</label>
              <Input
                type="file"
                data-testid="checkin-file-input"
                onChange={(e) => setCheckinFile(e.target.files[0])}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload the updated version of this document
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="checkin-submit-button"
                disabled={checkingIn}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {checkingIn ? 'Checking in...' : 'Check In'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCheckinDialogOpen(false);
                  setCheckinFile(null);
                  setSelectedDoc(null);
                }}
                disabled={checkingIn}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Metadata Autopilot Dialog */}
      <Dialog open={aiAutopilotOpen} onOpenChange={setAiAutopilotOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ai-autopilot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Metadata Autopilot
            </DialogTitle>
            <DialogDescription>
              AI has analyzed your document and suggested metadata. Review and approve or edit the suggestions below.
            </DialogDescription>
          </DialogHeader>
          
          {loadingSuggestions ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">AI is analyzing your document...</p>
              <p className="text-sm text-slate-400 mt-1">This may take a few seconds</p>
            </div>
          ) : aiSuggestions ? (
            <div className="space-y-4">
              {/* Suggested Title */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Suggested Title</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingField(editingField === 'title' ? null : 'title')}
                    data-testid="edit-title-btn"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                </div>
                {editingField === 'title' ? (
                  <Input
                    value={editedValues.title || ''}
                    onChange={(e) => handleEditField('title', e.target.value)}
                    data-testid="edit-title-input"
                    className="bg-white"
                  />
                ) : (
                  <p className="text-slate-900 font-medium" data-testid="suggested-title">
                    {editedValues.title || 'No suggestion'}
                  </p>
                )}
              </div>

              {/* Suggested Document Type */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Suggested Type</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingField(editingField === 'doc_type' ? null : 'doc_type')}
                    data-testid="edit-type-btn"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                </div>
                {editingField === 'doc_type' ? (
                  <Select 
                    value={editedValues.doc_type || ''} 
                    onValueChange={(val) => handleEditField('doc_type', val)}
                  >
                    <SelectTrigger data-testid="edit-type-select" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Invoice">Invoice</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Policy">Policy</SelectItem>
                      <SelectItem value="Technical Manual">Technical Manual</SelectItem>
                      <SelectItem value="Report">Report</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm" data-testid="suggested-type">
                    {editedValues.doc_type || 'No suggestion'}
                  </span>
                )}
              </div>

              {/* Suggested Tags */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Suggested Tags</label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingField(editingField === 'tags' ? null : 'tags')}
                    data-testid="edit-tags-btn"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                </div>
                {editingField === 'tags' ? (
                  <Input
                    value={Array.isArray(editedValues.tags) ? editedValues.tags.join(', ') : (editedValues.tags || '')}
                    onChange={(e) => handleEditField('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                    placeholder="tag1, tag2, tag3"
                    data-testid="edit-tags-input"
                    className="bg-white"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="suggested-tags">
                    {(Array.isArray(editedValues.tags) ? editedValues.tags : []).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                        {tag}
                      </span>
                    ))}
                    {(!editedValues.tags || editedValues.tags.length === 0) && (
                      <span className="text-slate-400 text-sm">No tags suggested</span>
                    )}
                  </div>
                )}
              </div>

              {/* Extracted Fields (if any) */}
              {editedValues.fields && Object.keys(editedValues.fields).length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Extracted Fields</label>
                  <div className="space-y-2">
                    {Object.entries(editedValues.fields).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="text-slate-900 font-medium">{renderSafe(value, 100) || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleAcceptSuggestions}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="accept-suggestions-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept & Apply
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRejectSuggestions}
                  data-testid="reject-suggestions-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No AI suggestions available yet.</p>
              <p className="text-sm text-slate-400 mt-1">The document may still be processing.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setAiAutopilotOpen(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the document and all its versions from the system and
              storage. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-doc-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="delete-doc-confirm"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Documents;
